import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactPlayer from "react-player/lazy";
import { ArrowLeft, CheckCircle2, Download, ExternalLink, Link2, Play, X } from "lucide-react";
import { useAuth } from "@/features/auth";
import { SELF_CARE_CHALLENGE_VIDEOS } from "./selfCareChallengeVideos";

const STORAGE_VERSION = "v1";

function safeParseJson(raw, fallback) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function getUserKey(user) {
  return user?._id || user?.email || "unknown";
}

function getStorageKey(user) {
  return `selfcare_challenge_progress:${STORAGE_VERSION}:${getUserKey(user)}`;
}

function useVimeoThumbnail(vimeoUrl) {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);

  useEffect(() => {
    if (!vimeoUrl) {
      return undefined;
    }

    let cancelled = false;

    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data?.thumbnail_url) {
          setThumbnailUrl(data.thumbnail_url);
        }
      })
      .catch(() => {
        // Thumbnail is a nice-to-have - fall back to the placeholder icon on any failure
        // (including a video whose embed-domain privacy setting also blocks oEmbed metadata).
      });

    return () => {
      cancelled = true;
    };
  }, [vimeoUrl]);

  return thumbnailUrl;
}

function VideoModal({ video, onClose, onEnded }) {
  const [embedBlocked, setEmbedBlocked] = useState(false);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-2xl rounded-lg bg-black shadow-[0_20px_60px_rgba(18,45,70,0.35)]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={video.title}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#1E2D3D] shadow"
          aria-label="Close video"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="aspect-video w-full overflow-hidden rounded-lg">
          {embedBlocked ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center text-white">
              <p className="m-0 text-sm">
                This video can&apos;t be played here. You can still watch it on
                Vimeo.
              </p>
              <a
                href={video.vimeoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onEnded}
                className="flex items-center gap-1.5 rounded bg-white px-4 py-2 text-sm font-semibold text-[#1E2D3D]"
              >
                Watch on Vimeo <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          ) : (
            <ReactPlayer
              url={video.vimeoUrl}
              controls
              playing
              width="100%"
              height="100%"
              onEnded={onEnded}
              onError={() => setEmbedBlocked(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function DayNoteBanner({ note }) {
  return (
    <div className="col-span-full rounded-lg border border-[#BFE3D3] bg-[#F2FBF7] p-5">
      <h2 className="m-0 text-base font-semibold text-[#0f5a41] sm:text-lg">{note.heading}</h2>
      {note.body.map((paragraph, index) => (
        <p key={index} className="mt-2 text-base text-[#1E2D3D]">
          {paragraph}
        </p>
      ))}
      {note.subheading && (
        <p className="mt-4 text-base font-semibold text-[#1E2D3D]">{note.subheading}</p>
      )}
      {note.list && (
        <ul className="mt-2 flex flex-col gap-2 text-base text-[#1E2D3D]">
          {note.list.map((item) => (
            <li key={item.label} className="flex gap-2">
              <span className="font-semibold">{item.label}</span>
              <span>
                {item.parts.map((part, index) =>
                  typeof part === "string" ? (
                    <span key={index}>{part}</span>
                  ) : (
                    <a
                      key={index}
                      href={part.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-[#3387CF] underline"
                    >
                      {part.text}
                    </a>
                  ),
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {note.footer && <p className="mt-4 text-base italic text-[#555555]">{note.footer}</p>}
    </div>
  );
}

function CourseIntro() {
  return (
    <div className="mb-6 w-full rounded-xl border border-[#BFE3D3] bg-[#F2FBF7] p-6 text-center sm:p-8">
      <h2 className="mb-3 text-lg font-medium sm:text-xl xl:text-3xl">
        Welcome to the 21-Day Self-Care Challenge!
      </h2>

       <p className="m-0 text-base sm:text-lg">
        You deserve the cascading benefits of 1-2-3 Wellness which will
        ripple into different aspects of your life!
      </p>

      <a
        href="https://drive.google.com/file/d/147T2qgN_6pnUgg5gP6klxESBCGNeRi6f/view?usp=sharing"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#00b27a] px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#009c6a]"
      >
        <Download className="h-4 w-4" /> Download Your E-Workbook
      </a>

      <p className="mt-5 w-full text-left text-sm sm:text-base text-[#1E2D3D] sm:text-center">
        To begin, please click the green button above to{" "}
        <strong>download your e-workbook and save it to your desktop</strong>.
        For best results using the e-workbook, we recommend downloading the
        FREE version of{" "}
        <a
          href="https://get2.adobe.com/reader/otherversions/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#3387CF] underline"
        >
          Adobe Reader
        </a>
        . Your workbook corresponds to the videos below. This 21-day
        Self-care Challenge is offered for you for FREE thanks to your
        school district or organization. Each of the 21 videos is about 5
        minutes. A short &ldquo;challenge&rdquo; is offered after each
        video that corresponds with your workbook.
      </p>

      <div className="mt-5 w-full rounded-lg border border-[#DDDDDD] bg-gradient-to-b from-[#F2FBF7] to-white p-4 text-left">
        <div className="flex flex-col gap-2 sm:gap-0">
          <span className="inline-block w-fit rounded-full bg-[#DDDDDD] px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide">
            Optional
          </span>
          <img
            src="/images/resources/lindenwood-university.webp"
            alt="Lindenwood University logo"
            className="h-auto w-32 self-center object-contain sm:w-56 xl:w-56 sm:pb-2"
          />
        </div>
        <p className="m-0 mt-2 text-sm sm:text-base text-[#1E2D3D]">
          1-2-3 Wellness is proud to partner with Lindenwood University to offer you the{" "}
          <strong>option of earning three graduate credits</strong>. If you
          would like to earn graduate credit for participation, you are
          responsible for the course/grad credit fee and assignments
          listed on page 6 of your workbook. Interested educators can
          visit{" "}
          <a
            href="https://www.credit4teachers.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#3387CF] underline"
          >
            Credit4teachers
          </a>{" "}
          to register and pay for the &ldquo;1-2-3 Wellness video
          course&rdquo;. All fees, including University fees, are included
          and you can coupon code{" "}
          <code className="rounded px-1.5 py-0.5 font-mono text-sm font-medium ">
            123school
          </code>{" "}
          as an added bonus.
        </p>
      </div>
    </div>
  );
}

function DayCard({ video, isWatched, watchedMap, onSelectVideo }) {
  const isAvailable = Boolean(video.vimeoUrl);
  const thumbnailUrl = useVimeoThumbnail(video.vimeoUrl);

  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-[#DDDDDD] bg-white p-4 hover:shadow-md">
      <button
        type="button"
        disabled={!isAvailable}
        onClick={() => onSelectVideo(video)}
        className={"flex w-full flex-col items-start gap-2 text-left transition cursor-pointer"}
      >
        <div className="flex w-full items-center justify-between">
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-[#3387CF]">
            Day {video.day}
          </span>
          {isWatched && (
            <span className="flex items-center gap-1 text-xs sm:text-sm font-semibold text-[#00b27a]">
              <CheckCircle2 className="h-4 w-4" /> Watched
            </span>
          )}
        </div>
        <div
          className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded bg-[#EAF4FF] bg-cover bg-center text-[#3387CF]"
          style={thumbnailUrl ? { backgroundImage: `url(${thumbnailUrl})` } : undefined}
        >
          {thumbnailUrl && <div className="absolute inset-0 bg-black/25" />}
          <Play className={`relative h-8 w-8 ${thumbnailUrl ? "text-white drop-shadow-md" : ""}`} />
        </div>
        <p className="m-0 text-sm sm:text-base font-semibold text-[#1E2D3D]">
          {isAvailable ? video.title : "Coming soon"}
        </p>
        {video.durationLabel && (
          <span className="text-xs sm:text-sm text-gray-500">{video.durationLabel}</span>
        )}
      </button>

      {video.extras?.length > 0 && (
        <div className="flex w-full flex-col items-start gap-1.5 border-t border-[#EEEEEE] pt-2">
          {video.extras.map((extra) => {
            const extraWatched = Boolean(watchedMap[extra.id]);

            if (extra.kind === "link") {
              return (
                <a
                  key={extra.id}
                  href={extra.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs font-semibold text-[#3387CF] hover:underline"
                >
                  <Link2 className="h-3.5 w-3.5" /> {extra.label}
                </a>
              );
            }

            return (
              <button
                key={extra.id}
                type="button"
                onClick={() =>
                  onSelectVideo({
                    id: extra.id,
                    title: extra.label,
                    durationLabel: extra.durationLabel,
                    vimeoUrl: extra.vimeoUrl,
                  })
                }
                className="flex items-center gap-1 text-left text-xs font-semibold text-[#3387CF] hover:underline"
              >
                <Play className="h-3.5 w-3.5" /> {extra.label}
                {extraWatched && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SelfCareChallengePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const storageKey = useMemo(() => getStorageKey(user), [user]);

  const [watchedMap, setWatchedMap] = useState({});
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    setWatchedMap(safeParseJson(window.localStorage.getItem(storageKey), {}));
  }, [storageKey]);

  const totalCount = SELF_CARE_CHALLENGE_VIDEOS.length;
  const watchedCount = SELF_CARE_CHALLENGE_VIDEOS.filter(
    (video) => watchedMap[video.id],
  ).length;

  const markWatched = (videoId) => {
    setWatchedMap((prev) => {
      if (prev[videoId]) {
        return prev;
      }
      const next = { ...prev, [videoId]: true };
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // localStorage may be unavailable (e.g. private browsing) - progress just won't persist
      }
      return next;
    });
  };

  return (
    <div className="tw">
      <div className="flex min-h-screen flex-col border border-[#DDDDDD] bg-[#F4F4F3] sm:ml-[130px] sm:mt-0 mt-16">
        <div className="sticky top-0 z-10 flex w-full flex-col gap-1 bg-white px-3 py-3 shadow sm:px-8 sm:py-4">
          <button
            type="button"
            onClick={() => navigate("/resourcestab")}
            className="flex w-fit items-center gap-1 text-sm font-medium text-[#3387CF]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Resources
          </button>
          <h1 className="text-xl font-medium sm:text-2xl">21-Day Self-Care Challenge</h1>
          <p className="m-0 text-sm text-gray-600">
            {watchedCount} of {totalCount} videos watched
          </p>
        </div>

        <div className="flex-1 px-3 pb-10 pt-4 sm:px-8">
          <CourseIntro />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {SELF_CARE_CHALLENGE_VIDEOS.map((video) => (
              <Fragment key={video.id}>
                <DayCard
                  video={video}
                  isWatched={Boolean(watchedMap[video.id])}
                  watchedMap={watchedMap}
                  onSelectVideo={setActiveVideo}
                />
                {video.note && <DayNoteBanner note={video.note} />}
              </Fragment>
            ))}
          </div>
        </div>
      </div>

      {activeVideo && (
        <VideoModal
          video={activeVideo}
          onClose={() => setActiveVideo(null)}
          onEnded={() => markWatched(activeVideo.id)}
        />
      )}
    </div>
  );
}
